/**
 * Single source of truth for the seven front-end status pills shown in the
 * Verification Center table and the Applicant page.
 *
 * Operator-facing pill labels:
 *   New • In Progress • Waiting on Applicant • Needs Review
 *   Verified • Not Verified • Closed
 *
 * Recruiting orgs read "Reviewed, no findings" and "Reviewed, findings noted"
 * in place of Verified and Not Verified. Pass the operator's vertical to
 * getCaseStatus(); see "Recruiting words for the review outcome" below.
 *
 * Hard language rules (operator UI only — see docs/case-lifecycle-rules.md):
 *   - "Blocked" never appears in the UI. Internal-only concept; kept as
 *     the `action_required` StatusKind.
 *   - "Submitted" never appears as a pill label — use "Needs Review".
 *   - "Denied" / "Deny" / "Declined" never appear as operator outcomes.
 *     Use "Not Verified" for outcomes and "Closed" for lifecycle.
 *   - "Pending" never appears alone — always pair with a subject
 *     ("Ready for team review", "Waiting for applicant to…").
 *   - Operator-facing copy uses "Vouch", not "Reference".
 *   - No "approved", "rejected", "passed", "failed", "decisioned" copy.
 *   - "Not Verified" means identity confidence was not established — it is
 *     NOT a denial of the rental application.
 *   - "Closed" is reserved for cases that ended without a completed
 *     DigaVouch verification result.
 */

import type { CaseSummary } from "@/lib/api";
import { DEFAULT_VERTICAL, isRecruiting, type Vertical } from "@/lib/verticalCopy";
import {
  AlertTriangle,
  Archive,
  Clock,
  FileText,
  History,
  ShieldCheck,
  ShieldMinus,
  Users,
  type LucideIcon,
} from "lucide-react";


export type StatusKind =
  | "pending"
  | "in_progress"
  | "submitted"
  | "awaiting_vouches"
  | "action_required"
  | "verified"
  | "not_verified"
  | "closed";

export interface DigaVouchResult {
  label: "Verified" | "Not Verified" | "No result" | "Pending";
  body: string;
}

export type NextStepActionKind =
  | "review"
  | "request_more"
  | "resend_link"
  | "send_reminder"
  | "view_report"
  | "add_note"
  | "reopen"
  | "new_link"
  | "follow_up";

export interface NextStepAction {
  label: string;
  kind: NextStepActionKind;
  primary?: boolean;
}

export interface CaseStatusInfo {
  kind: StatusKind;
  pill: string;
  description: string;
  dotClass: string;
  pillClass: string;
  tileClass: string;
  /** Lucide icon component for the pill — provides non-color status cue. */
  Icon: LucideIcon;
  /** True when the icon should spin (in-progress only). */
  iconSpin?: boolean;
  result: DigaVouchResult;
  nextStep?: string;
  /** Body paragraph for the Recommended Next Step card. Falls back to result.body when absent. */
  nextStepBody?: string;
  /** Optional smaller microcopy line below the body (used for Waiting-on-Applicant today). */
  nextStepMicro?: string;
  nextStepActions: NextStepAction[];
  showRegulatoryNote: boolean;
  /** Lower number = higher triage priority (used by default sorting). */
  priority: number;
}


export const REGULATORY_NOTE =
  "DigaVouch does not approve or deny rental applications. This result only reflects whether identity confidence was established through the applicant's verification flow. Final application decisions remain with the property team and should follow the property's standard leasing policy.";

const RESULT_PENDING: DigaVouchResult = {
  label: "Pending",
  body: "A result will appear once verification is complete and the case is reviewed.",
};

const RESULT_VERIFIED: DigaVouchResult = {
  label: "Verified",
  body: "Identity confidence was established based on the applicant's connected signals and review activity.",
};

const RESULT_NOT_VERIFIED: DigaVouchResult = {
  label: "Not Verified",
  body: "Identity confidence was not established based on the available verification signals.",
};

const RESULT_NO_RESULT: DigaVouchResult = {
  label: "No result",
  body: "This case was closed before a verification result was completed.",
};

/**
 * Pill color tokens. AA contrast (≥4.5:1) and red-free — Blocked uses amber,
 * Not Verified uses light pink, each paired with a unique icon so color is
 * never the sole carrier of meaning.
 *
 *   New          → slate (light gray)
 *   In Progress  → light sky blue
 *   To Review    → darker purple (violet)
 *   Blocked      → amber  (never red)
 *   Verified     → emerald green
 *   Not Verified → light pink (never red)
 *   Closed       → slate gray
 */
const PILL_CLASS: Record<StatusKind, string> = {
  pending: "bg-[#F1F5F9] text-[#475569] border border-[#CBD5E1]",
  in_progress: "bg-sky-50 text-sky-700 border border-sky-200",
  submitted: "bg-violet-100 text-violet-900 border border-violet-300",
  awaiting_vouches: "bg-orange-100 text-orange-900 border border-orange-300",
  action_required: "bg-amber-50 text-amber-900 border border-amber-300",
  verified: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  not_verified: "bg-pink-50 text-pink-800 border border-pink-200",
  closed: "bg-slate-100 text-slate-700 border border-slate-300",
};

const DOT_CLASS: Record<StatusKind, string> = {
  pending: "bg-[#64748B]",
  in_progress: "bg-sky-500",
  submitted: "bg-violet-700",
  awaiting_vouches: "bg-orange-500",
  action_required: "bg-amber-600",
  verified: "bg-emerald-600",
  not_verified: "bg-pink-600",
  closed: "bg-slate-500",
};

const TILE_CLASS: Record<StatusKind, string> = {
  pending: "bg-[#F1F5F9] text-[#475569] border border-[#CBD5E1]",
  in_progress: "bg-sky-50 text-sky-700 border border-sky-200",
  submitted: "bg-violet-100 text-violet-900 border border-violet-300",
  awaiting_vouches: "bg-orange-100 text-orange-900 border border-orange-300",
  action_required: "bg-amber-50 text-amber-900 border border-amber-300",
  verified: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  not_verified: "bg-pink-50 text-pink-800 border border-pink-200",
  closed: "bg-slate-100 text-slate-700 border border-slate-300",
};

/**
 * Non-color status cue. Each pill renders this icon alongside the label so
 * users who can't distinguish color (grayscale, deuteranopia, protanopia)
 * still get a unique visual cue per status.
 */
const ICON: Record<StatusKind, LucideIcon> = {
  pending: Clock,
  in_progress: History,
  submitted: FileText,
  awaiting_vouches: Users,
  action_required: AlertTriangle,
  verified: ShieldCheck,
  not_verified: ShieldMinus,
  closed: Archive,
};

const PILL_LABEL: Record<StatusKind, string> = {
  pending: "New",
  in_progress: "In Progress",
  submitted: "Needs Review",
  awaiting_vouches: "Awaiting vouches",
  action_required: "Waiting on Applicant",
  verified: "Verified",
  not_verified: "Not Verified",
  closed: "Closed",
};

/** Tooltip copy shown when hovering a row pill (applicant-specific phrasing). */
export const ROW_TOOLTIP: Record<StatusKind, string> = {
  pending: "Invite sent — applicant has not started yet.",
  in_progress: "Applicant is completing verification.",
  submitted: "Ready for the team to review.",
  awaiting_vouches: "Applicant submitted — waiting on outstanding vouches before review.",
  action_required: "Waiting on the applicant to complete an action.",
  verified: "DigaVouch established identity confidence for this applicant.",
  not_verified: "DigaVouch could not establish identity confidence for this applicant.",
  closed: "Request ended without a verification outcome.",
};

/** Tooltip copy shown when hovering a filter pill (filter-specific phrasing). */
export const FILTER_TOOLTIP: Record<StatusKind | "all", string> = {
  all: "Show all verification requests.",
  pending: "Show invites sent but not yet started.",
  in_progress: "Show applicants currently completing verification.",
  submitted: "Show cases ready for the team to review.",
  awaiting_vouches: "Show submitted cases still waiting on outstanding vouches.",
  action_required: "Show cases waiting on the applicant to complete an action.",
  verified: "Show cases where identity confidence was established.",
  not_verified: "Show cases where identity confidence was not established.",
  closed: "Show cases that ended without a verification outcome.",
};

const PRIORITY: Record<StatusKind, number> = {
  action_required: 1,
  submitted: 2,
  awaiting_vouches: 3,
  in_progress: 4,
  pending: 5,
  verified: 6,
  not_verified: 6,
  closed: 7,
};

/** Filter dropdown / tab options in the order operators read the queue. */
export const STATUS_FILTER_OPTIONS: { value: StatusKind | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "action_required", label: "Waiting on Applicant" },
  { value: "submitted", label: "Needs Review" },
  { value: "awaiting_vouches", label: "Awaiting vouches" },
  { value: "verified", label: "Verified" },
  { value: "not_verified", label: "Not Verified" },
  { value: "closed", label: "Closed" },
];

/** Order used for the status summary strip above the table. */
export const SUMMARY_ORDER: StatusKind[] = [
  "submitted",
  "awaiting_vouches",
  "in_progress",
  "action_required",
  "pending",
  "verified",
  "not_verified",
  "closed",
];

function build(
  kind: StatusKind,
  description: string,
  result: DigaVouchResult,
  opts?: {
    nextStep?: string;
    nextStepBody?: string;
    nextStepMicro?: string;
    nextStepActions?: NextStepAction[];
    showRegulatoryNote?: boolean;
  },
): CaseStatusInfo {
  return {
    kind,
    pill: PILL_LABEL[kind],
    description,
    dotClass: DOT_CLASS[kind],
    pillClass: PILL_CLASS[kind],
    tileClass: TILE_CLASS[kind],
    Icon: ICON[kind],
    iconSpin: false,
    result,
    nextStep: opts?.nextStep,
    nextStepBody: opts?.nextStepBody,
    nextStepMicro: opts?.nextStepMicro,
    nextStepActions: opts?.nextStepActions ?? [],
    showRegulatoryNote: opts?.showRegulatoryNote ?? false,
    priority: PRIORITY[kind],
  };
}


/**
 * Whole days elapsed since the invite was created. Used for the
 * Day 0 / Day 1 / Day 2 / Day 3+ New-invite copy and for the FE-derived
 * "invite expired" lifecycle transition (until backend §C ships).
 */
export function daysSinceCreated(c: CaseSummary): number {
  const iso = c.created_at;
  if (!iso) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

function newInviteDescription(c: CaseSummary): string {
  const days = daysSinceCreated(c);
  if (days <= 0) return "Invite sent · applicant has not started";
  if (days === 1) return "Invite sent · 2 days left to start";
  if (days === 2) return "Invite sent · 1 day left to start";
  // Day 3+ is handled by the Closed branch via inviteExpired() — this
  // string is a safety net if the caller short-circuits.
  return "Closed · invite expired";
}

function inProgressDescription(c: CaseSummary): string {
  const n = c.signals_verified_count ?? c.signals_verified?.length ?? 0;
  if (n <= 0) return "Applicant started verification";
  return `Applicant has connected ${n} signal${n === 1 ? "" : "s"}`;
}

function waitingOnApplicantDescription(_c: CaseSummary): string {
  // Follow-up reason → concrete subject map could land here once backend
  // exposes structured follow-up subjects. For now keep one clear line.
  return "Waiting for applicant to complete additional verification";
}

function needsReviewDescription(_c: CaseSummary): string {
  // Per-vouch sub-copy ("Vouch completed · ready for review", etc.)
  // unlocks once CaseSummary.vouch_summary lands (backend follow-up §B).
  return "Ready for team review";
}

/** True when a New invite has aged past the 3-day cutoff. */
function inviteExpired(c: CaseSummary): boolean {
  return c.status === "pending_consent" && daysSinceCreated(c) >= 3;
}

function closedDescription(c: CaseSummary): string {
  // `declined_by` is the authoritative actor signal — check it BEFORE the
  // bare `status === "declined"` fallback, otherwise operator-closed cases
  // (which come back as status="declined" + declined_by="operator") get
  // mislabelled as "Applicant withdrew".
  if (c.declined_by === "operator" || c.operator_action === "denied") return "Closed by team";
  if (c.declined_by === "applicant") return "Applicant withdrew";
  if (c.status === "expired" || inviteExpired(c)) return "Closed · invite expired";
  if (c.status === "declined") return "Applicant withdrew";
  return "Request ended without verification";
}

/**
 * Translate a backend CaseSummary into the seven-state UI status.
 * Used by the Verification Center table and the Applicant page so the
 * pill and description never drift between views.
 */
export function getCaseStatus(
  c: CaseSummary,
  vertical: Vertical = DEFAULT_VERTICAL,
): CaseStatusInfo {
  const info = getLeasingCaseStatus(c);
  return isRecruiting(vertical) ? applyRecruitingWords(info) : info;
}

function getLeasingCaseStatus(c: CaseSummary): CaseStatusInfo {
  // Operator decision takes precedence over zone.
  if (c.operator_action === "approved") {
    return build("verified", "Identity confidence verified", RESULT_VERIFIED, {
      showRegulatoryNote: true,
      nextStep: "Continue your standard leasing workflow.",
      nextStepActions: [
        { label: "View verification report", kind: "view_report", primary: true },
      ],
    });
  }

  if (c.operator_action === "denied" && c.status === "completed") {
    return build("not_verified", "Unable to verify identity confidence", RESULT_NOT_VERIFIED, {
      showRegulatoryNote: true,
      nextStep: "Review according to your property's standard policy.",
      nextStepActions: [
        { label: "View verification report", kind: "view_report", primary: true },
        { label: "Add internal note", kind: "add_note" },
      ],
    });
  }

  // FE-derived expiry for New invites past Day 3. Backend §C will replace
  // this with a server-side transition to status === "expired".
  // `declined_by` is the strongest closed signal — if the operator (or
  // applicant) explicitly ended the case, it's Closed regardless of the
  // residual `status` / `operator_action` values the backend left behind
  // (e.g. status="completed" + operator_action="requested_more_info" +
  // declined_by="operator" after the operator subsequently closed it).
  if (
    c.status === "declined" ||
    c.status === "expired" ||
    c.operator_action === "denied" ||
    c.declined_by === "operator" ||
    c.declined_by === "applicant" ||
    inviteExpired(c)
  ) {
    return build("closed", closedDescription(c), RESULT_NO_RESULT, {
      showRegulatoryNote: true,
      nextStep:
        "No DigaVouch result was produced. Follow your property's process for incomplete verification.",
      nextStepActions: [
        { label: "Send new verification link", kind: "new_link", primary: true },
        { label: "Add internal note", kind: "add_note" },
      ],
    });
  }

  // Waiting on Applicant — canonical signal + today's operator_action path.
  if (
    c.status === "step_up_required" ||
    c.operator_action === "requested_more_info"
  ) {
    return build(
      "action_required",
      waitingOnApplicantDescription(c),
      RESULT_PENDING,
      {
        nextStep: "Waiting on applicant",
        nextStepBody:
          "Additional verification has already been requested. The applicant still needs to complete the extra verification you requested (for example, a human vouch, a new document, or a verified account) before this case is ready for review.",
        nextStepMicro:
          "The applicant has already been asked for more information. Use the actions below to send a reminder or close the request.",
        nextStepActions: [
          { label: "Send applicant reminder", kind: "send_reminder", primary: true },
          { label: "Add internal note", kind: "add_note" },
        ],
      },

    );
  }

  // Submitted, but one or more requested vouches haven't come back yet — not
  // ready for operator review. Distinct from "Needs Review" (submitted + ready)
  // and "Waiting on Applicant" (the applicant still owes an action). The backend
  // emits this only while the inquiry is stepup_in_progress; it promotes to
  // 'completed' once completedVouches >= requiredVouches.
  if (c.status === "awaiting_vouches") {
    return build(
      "awaiting_vouches",
      "The applicant submitted and finished their part; one or more requested vouches are still outstanding.",
      RESULT_PENDING,
      {
        nextStep: "Waiting on outstanding vouches",
        nextStepBody:
          "The applicant has completed their verification. This case becomes ready for review once the outstanding vouch(es) are returned. You can wait for the vouchers to respond, or add an internal note.",
        nextStepActions: [{ label: "Add internal note", kind: "add_note" }],
      },
    );
  }

  if (c.status === "completed") {
    // All completed cases land in Needs Review regardless of zone (clear /
    // flag / gray). The operator is the sole decider of Verified vs
    // Not Verified — confidence zone is shown as a signal inside the
    // review, but never auto-routes the pill. Verified / Not Verified
    // pills are produced exclusively by the operator_action branches
    // above (approved / denied).
    return build("submitted", needsReviewDescription(c), RESULT_PENDING, {
      nextStep: "Ready for team review",
      nextStepBody:
        "Review the Identity Confidence card and the connected signals. If the vouch and accounts look consistent with the application, you can mark this applicant as Verified. If something feels off or important signals are missing, you can request more information or mark the applicant as Not Verified using the actions below.",
      nextStepActions: [
        { label: "Review verification", kind: "review", primary: true },
        { label: "Request more info", kind: "request_more" },
      ],
    });
  }

  if (c.status === "pending_consent") {
    return build("pending", newInviteDescription(c), RESULT_PENDING, {
      nextStep: "Invite sent",
      nextStepBody:
        "The applicant has not started verification yet. You can use the actions below to send a reminder or resend the verification link.",
      nextStepActions: [
        { label: "Resend verification link", kind: "resend_link", primary: true },
        { label: "Mark for follow-up", kind: "follow_up" },
      ],
    });
  }

  if (c.status === "in_progress") {
    return build("in_progress", inProgressDescription(c), RESULT_PENDING, {
      nextStep: "Applicant is still completing verification",
      nextStepBody:
        "The applicant has started but has not submitted yet. You can wait for them to finish or use the actions below to send a reminder if needed.",
      nextStepActions: [
        { label: "Send reminder", kind: "send_reminder" },
      ],
    });
  }

  return build("in_progress", "Applicant is completing verification", RESULT_PENDING, {
    nextStepActions: [{ label: "Send reminder", kind: "send_reminder" }],
  });

}

/**
 * Operator-facing "What's happening" copy. Thin wrapper over
 * getCaseStatus(c).description today — exists as a stable seam so the
 * Dashboard table and Applicant page can never drift, and so future
 * per-vouch / per-follow-up sub-copy can land in one place.
 */
export function buildWhatsHappening(
  c: CaseSummary,
  vertical: Vertical = DEFAULT_VERTICAL,
): string {
  return getCaseStatus(c, vertical).description;
}

// ------------------------------------------------------------------
// Recruiting words for the review outcome
// ------------------------------------------------------------------

/**
 * What a recruiter reads for the two review outcomes.
 *
 * In leasing, "Verified" and "Not Verified" are the outcome words and they
 * stay. A recruiter must not read a verdict, a confidence level or a grade:
 * they see the Trust Checkpoint state and the evidence, and the outcome is
 * their own team's review, so the words say exactly that. The StatusKind keys,
 * the tabs, the sort and the filters are unchanged; only the visible words
 * differ.
 *
 * Every recruiting word for the outcome lives in this block, so the next
 * rename is an edit here and nowhere else.
 */
const RECRUITING_PILL_LABEL: Partial<Record<StatusKind, string>> = {
  verified: "Reviewed, no findings",
  not_verified: "Reviewed, findings noted",
};

/**
 * The case page header pill. On that page the recorded findings (the reason
 * and any note) are shown further down, in the Recommended Next Step card, so
 * the pill can point at them. Everywhere else (queue pill, tab, tooltip,
 * toast) nothing sits below, so those use the short form above.
 */
const RECRUITING_DETAIL_PILL_LABEL: Partial<Record<StatusKind, string>> = {
  not_verified: "Reviewed, findings noted below",
};

const RECRUITING_FILTER_TOOLTIP: Partial<Record<StatusKind, string>> = {
  verified: "Show cases your team reviewed with no findings.",
  not_verified: "Show cases where your team noted findings.",
};

/** The visible word for a status, in the operator's vertical. */
export function statusLabel(kind: StatusKind, vertical: Vertical = DEFAULT_VERTICAL): string {
  return (isRecruiting(vertical) && RECRUITING_PILL_LABEL[kind]) || PILL_LABEL[kind];
}

/** The status pill on the case page header, in the operator's vertical. */
export function detailStatusLabel(
  kind: StatusKind,
  vertical: Vertical = DEFAULT_VERTICAL,
): string {
  return (isRecruiting(vertical) && RECRUITING_DETAIL_PILL_LABEL[kind]) || statusLabel(kind, vertical);
}

/** Filter-tab tooltip, in the operator's vertical. */
export function filterTooltip(
  kind: StatusKind | "all",
  vertical: Vertical = DEFAULT_VERTICAL,
): string {
  if (kind !== "all" && isRecruiting(vertical) && RECRUITING_FILTER_TOOLTIP[kind]) {
    return RECRUITING_FILTER_TOOLTIP[kind] as string;
  }
  return FILTER_TOOLTIP[kind];
}

/** `STATUS_FILTER_OPTIONS` with each label in the operator's vertical. */
export function statusFilterOptions(
  vertical: Vertical = DEFAULT_VERTICAL,
): { value: StatusKind | "all"; label: string }[] {
  return STATUS_FILTER_OPTIONS.map((o) =>
    o.value === "all" ? o : { ...o, label: statusLabel(o.value, vertical) },
  );
}

/**
 * The recruiting version of a case status. Same kind, same priority, same
 * colours and icon; only the words a recruiter reads change.
 */
function applyRecruitingWords(info: CaseStatusInfo): CaseStatusInfo {
  const pill = statusLabel(info.kind, "hr_recruiting");
  switch (info.kind) {
    case "verified":
      return {
        ...info,
        pill,
        description: "Reviewed by your team, no findings",
        result: {
          ...info.result,
          body: "Your team reviewed the evidence the applicant provided and recorded no findings.",
        },
        nextStep: "Continue your standard hiring process.",
      };
    case "not_verified":
      return {
        ...info,
        pill,
        description: "Reviewed by your team, findings noted",
        result: {
          ...info.result,
          body: "Your team reviewed the evidence the applicant provided and noted findings.",
        },
        nextStep: "Continue your standard hiring process.",
      };
    case "closed":
      return {
        ...info,
        result: {
          ...info.result,
          body: "This case was closed before the Trust Checkpoint was completed.",
        },
        nextStep:
          "The Trust Checkpoint was not completed. Continue your standard hiring process.",
      };
    case "submitted":
      return {
        ...info,
        nextStepBody:
          "Open the Identity Evidence card to see what the applicant provided and what is still missing. When you are done, record your review below: no findings, findings noted, or a request for more information.",
      };
    default:
      return info;
  }
}

/**
 * The decision controls on the operator detail page, in the operator's
 * vertical. Leasing strings are exactly what the page has always shown.
 */
export interface ReviewDecisionCopy {
  proceedLabel: string;
  proceedHint: string;
  holdLabel: string;
  holdHint: string;
  /** Title of the reason dialog behind the hold control. */
  holdDialogTitle: string;
  holdDialogBody: string;
  /** Confirmation toast after each decision is saved. */
  proceedToast: string;
  holdToast: string;
}

const LEASING_DECISION_COPY: ReviewDecisionCopy = {
  proceedLabel: "Mark as Verified",
  proceedHint: "Signals are sufficient to establish identity confidence.",
  holdLabel: "Mark as Not Verified",
  holdHint: "Review complete — identity confidence was not established.",
  holdDialogTitle: "Reason Identity Was Not Verified",
  holdDialogBody:
    "Select the reason that best explains why identity confidence was not established. This reason reflects the identity-verification outcome only and is not the final leasing decision.",
  proceedToast: "Marked as verified",
  holdToast: "Marked as not verified",
};

const RECRUITING_DECISION_COPY: ReviewDecisionCopy = {
  proceedLabel: "Mark reviewed, no findings",
  proceedHint: "You reviewed the evidence and have no findings to record.",
  holdLabel: "Mark reviewed, findings noted",
  holdHint: "You reviewed the evidence and want to record findings.",
  holdDialogTitle: "Record findings",
  holdDialogBody:
    "Select the reason that best describes your findings. It is saved to the case history.",
  proceedToast: "Recorded: reviewed, no findings",
  holdToast: "Recorded: findings noted",
};

export function reviewDecisionCopy(vertical: Vertical = DEFAULT_VERTICAL): ReviewDecisionCopy {
  return isRecruiting(vertical) ? RECRUITING_DECISION_COPY : LEASING_DECISION_COPY;
}

export function isClosedCase(c: CaseSummary): boolean {
  // Must mirror the Closed branch in getCaseStatus() exactly, otherwise the
  // dashboard "All" / "Closed" filter desyncs from the pill (e.g. a case
  // with status="completed" + declined_by="operator" reads "Closed by team"
  // in the pill but leaks into the All tab).
  //
  // IMPORTANT: `operator_action === "denied"` on a COMPLETED case is
  // **Not Verified**, not Closed — getCaseStatus() short-circuits on that
  // branch first. Only treat denied as Closed when the case never reached
  // completed (i.e. the operator killed it before submission).
  return (
    c.status === "declined" ||
    c.status === "expired" ||
    (c.operator_action === "denied" && c.status !== "completed") ||
    c.declined_by === "operator" ||
    c.declined_by === "applicant" ||
    inviteExpired(c)
  );
}


/**
 * Human-friendly relative time. Falls back to absolute date for >= 48h.
 */
export function formatRelativeTime(iso?: string): string {
  if (!iso) return "[needs data]";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "[needs data]";
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  if (hours < 48) return "Yesterday";
  const now = new Date();
  const isCurrentYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    ...(isCurrentYear ? {} : { year: "numeric" }),
  });
}

/** Absolute timestamp for tooltips/hover, in the user's locale. */
export function formatAbsoluteDateTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

/** Absolute date only (no time), in the user's locale. */
export function formatAbsoluteDate(iso?: string): string {
  if (!iso) return "[needs data]";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "[needs data]";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
